import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

interface MobileTextComposerProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: (value: string) => void;
  placeholder: string;
  hidePlaceholderOnFocus?: boolean;
  disabled?: boolean;
  autoFocus?: boolean;
  placeCaretAtEndOnAutoFocus?: boolean;
  preventFocusScroll?: boolean;
  isDark?: boolean;
  onFocusIntent?: () => void;
  className?: string;
  textareaClassName?: string;
  emptyActionIcon?: React.ReactNode;
  emptyActionAriaLabel?: string;
  onEmptyAction?: () => void;
  hideAction?: boolean;
  actionButtonClassName?: string;
  actionInnerClassName?: string;
  sendIconClassName?: string;
}

const ArrowUpIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className || 'h-5 w-5'} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M12 19V5m0 0-6 6m6-6 6 6"
      stroke="currentColor"
      strokeWidth="2.25"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const MobileTextComposer = React.forwardRef<HTMLTextAreaElement, MobileTextComposerProps>(({
  value,
  onChange,
  onSubmit,
  placeholder,
  hidePlaceholderOnFocus = false,
  disabled = false,
  autoFocus = false,
  placeCaretAtEndOnAutoFocus = false,
  preventFocusScroll = false,
  isDark = false,
  onFocusIntent,
  className = '',
  textareaClassName = '',
  emptyActionIcon,
  emptyActionAriaLabel = 'Switch input mode',
  onEmptyAction,
  hideAction = false,
  actionButtonClassName = '',
  actionInnerClassName = '',
  sendIconClassName = '',
}, forwardedRef) => {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const [isFocused, setIsFocused] = useState(false);
  const trimmed = value.trim();
  const hasText = trimmed.length > 0;
  const showEmptyAction = !hasText && !!onEmptyAction;

  const setTextareaRef = useCallback((element: HTMLTextAreaElement | null) => {
    textareaRef.current = element;
    if (typeof forwardedRef === 'function') {
      forwardedRef(element);
    } else if (forwardedRef) {
      forwardedRef.current = element;
    }
  }, [forwardedRef]);

  const autoSize = (element: HTMLTextAreaElement | null) => {
    if (!element) return;
    element.style.height = 'auto';
    element.style.height = `${element.scrollHeight}px`;
  };

  useLayoutEffect(() => {
    autoSize(textareaRef.current);
  }, [value]);

  const focusTextarea = useCallback((placeCaretAtEnd = false) => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    onFocusIntent?.();
    if (preventFocusScroll) textarea.focus({ preventScroll: true });
    else textarea.focus();

    if (placeCaretAtEnd) {
      const end = textarea.value.length;
      textarea.setSelectionRange(end, end);
    }
  }, [onFocusIntent, preventFocusScroll]);

  useEffect(() => {
    if (!autoFocus) return;
    focusTextarea(placeCaretAtEndOnAutoFocus);
  }, [autoFocus, focusTextarea, placeCaretAtEndOnAutoFocus]);

  const stopEvent = (event: React.SyntheticEvent) => {
    event.stopPropagation();
  };

  const submit = () => {
    if (!hasText || disabled) return;
    onSubmit(trimmed);
  };

  return (
    <div
      className={`flex items-center overflow-hidden rounded-[28px] text-blue-600 ${
        isDark
          ? 'border border-black-700 bg-black-900'
          : 'border-2 border-blue-600 bg-white'
      } ${className}`}
      onClick={stopEvent}
      onPointerDown={stopEvent}
      onTouchStart={stopEvent}
    >
      <textarea
        ref={setTextareaRef}
        value={value}
        autoComplete="off"
        data-bwignore="true"
        onPointerDown={(event) => {
          event.stopPropagation();
          if (preventFocusScroll) {
            onFocusIntent?.();
            event.currentTarget.focus({ preventScroll: true });
          }
        }}
        onChange={(event) => {
          onChange(event.target.value);
          autoSize(event.target);
        }}
        onFocus={(event) => {
          setIsFocused(true);
          stopEvent(event);
        }}
        onBlur={(event) => {
          setIsFocused(false);
          stopEvent(event);
        }}
        onKeyDown={(event) => {
          event.stopPropagation();
          if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            submit();
          }
        }}
        rows={1}
        placeholder={hidePlaceholderOnFocus && isFocused ? '' : placeholder}
        aria-label="Type a message to Aleph"
        className={`min-h-[52px] flex-1 resize-none overflow-y-hidden bg-transparent px-5 py-[14px] text-base leading-6 outline-none ${
          isDark
            ? 'text-white-custom placeholder:text-black-200'
            : 'text-gray-800 placeholder:text-gray-500'
        } ${textareaClassName}`}
      />
      {!hideAction && (
        <button
          type="button"
          onPointerDown={(event) => {
            event.stopPropagation();
            event.preventDefault();
          }}
          onClick={(event) => {
            event.stopPropagation();
            if (showEmptyAction) {
              onEmptyAction?.();
              return;
            }
            submit();
          }}
          disabled={!showEmptyAction && (!hasText || disabled)}
          aria-label={showEmptyAction ? emptyActionAriaLabel : 'Send message'}
          className={`flex w-16 shrink-0 cursor-pointer items-center justify-center self-stretch transition-colors ${
            !showEmptyAction && (!hasText || disabled) ? 'opacity-45' : ''
          } ${actionButtonClassName}`}
        >
          <span className={`flex h-10 w-10 items-center justify-center rounded-full bg-blue-600 text-white shadow-sm transition-colors ${actionInnerClassName}`}>
            {showEmptyAction && emptyActionIcon ? emptyActionIcon : <ArrowUpIcon className={`h-5 w-5 ${sendIconClassName}`} />}
          </span>
        </button>
      )}
    </div>
  );
});

MobileTextComposer.displayName = 'MobileTextComposer';

export default MobileTextComposer;
